import { useEffect, useRef, useState } from "react";
import { Globe, Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { logo } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { href: "#services", key: "nav.services" },
  { href: "#sectors", key: "nav.sectors" },
  { href: "#loans", key: "nav.loans" },
  { href: "#team", key: "nav.team" },
  { href: "#contact", key: "nav.contact" },
];

const LangSwitcher = ({ mobile = false }) => {
  const { lang, setLang, langs } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = langs.find((l) => l.code === lang);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="lang-switcher"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full border border-gold/30 px-3 py-2 text-sm text-gold-deep hover:border-gold/70 hover:bg-gold/5 transition-all duration-300"
        aria-label="Change language"
      >
        <Globe size={16} strokeWidth={1.75} />
        <span className="font-medium">{current ? current.code.toUpperCase() : "EN"}</span>
      </button>
      {open && (
        <div
          data-testid="lang-menu"
          className={`absolute ${mobile ? "bottom-full mb-2 left-0" : "top-full mt-2 right-0"} w-48 max-h-72 overflow-y-auto rounded-xl bg-[#0A1322] border border-gold/25 shadow-2xl shadow-black/15 py-2 z-50`}
        >
          {langs.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 ${
                l.code === lang
                  ? "text-gold-deep bg-gold/10"
                  : "text-slate-700 hover:text-gold-deep hover:bg-gold/5"
              }`}
            >
              {l.name}
              <span className="float-right text-xs text-slate-500 uppercase">{l.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Nav() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollToId(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass shadow-lg shadow-black/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={go("#top")}
            className="flex items-center gap-3 group"
          >
            <img
              src={logo}
              alt="Pacific Investment Group"
              className="h-11 w-11 rounded-lg object-cover ring-1 ring-gold/40 group-hover:ring-gold/80 transition-all duration-500"
            />
            <span className="leading-tight hidden sm:block">
              <span className="block font-display text-lg font-semibold tracking-wide text-navy-900">
                PACIFIC
              </span>
              <span className="block text-[10px] font-mono-brand tracking-[0.32em] text-gold-deep uppercase">
                Investment Group
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                onClick={go(l.href)}
                className="text-sm text-slate-700 hover:text-gold-deep transition-colors duration-300 relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full"
              >
                {t(l.key)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangSwitcher />
            <a
              href="#contact"
              data-testid="nav-cta-button"
              onClick={go("#contact")}
              className="btn-gold hidden sm:inline-flex rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              {t("nav.cta")}
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-navy-800 p-2"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden glass border-t border-gold/20" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={go(l.href)}
                className="py-3 text-navy-800 border-b border-white/5"
              >
                {t(l.key)}
              </a>
            ))}
            <a
              href="#contact"
              onClick={go("#contact")}
              className="btn-gold rounded-full px-5 py-3 text-sm font-semibold text-center mt-4"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
